import React, { useState, useEffect, useContext, useRef } from 'react';
import { Clock, Edit, Eye, History, UserPlus, Info } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import useLoader from '../../../../context/useLoader';
import processService from '../../../../services/processService';
import taskService from '../../../../services/taskService';
import { useNavigate } from 'react-router-dom';
import UpdateRiskModal from './ActivityRiskUpdatePopup';

export default function ProcessListItems({id, showActions}) {
  const navigate = useNavigate();
  const { user, hasRole, authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();
  const { isLoading, showLoader, hideLoader } = useLoader();
  
  const [process, setProcess] = useState(null);
  const [isCreatingTask, setIsCreatingTask] = useState(null);
  const [showControlPopup, setShowControlPopup] = useState(null);
  const popupRef = useRef(null);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [updateData, setUpdateData] = useState({
    probability: '',
    consequence: '',
    actionId: 0,
    action: '',
    dueDate: '',
    assignedUser: null,
    selectedRole: null
  });

  useEffect(() => {
    fetchProcessDetails();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowControlPopup(null);
      }
    };

    if (showControlPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showControlPopup]);

  const fetchProcessDetails = async () => {
    showLoader('Chargement des détails...');
    
    try {
      const result = await processService.getProcessDetails(id, authTokens.access);
      
      if (result.success) {
        setProcess(result.data);
      } else {
        showError(`Erreur lors du chargement: ${result.error}`);
      }
    } catch (error) {
      console.error('Error fetching process details:', error);
      showError('Impossible de charger les détails du processus');
    } finally {
      hideLoader();
    }
  };

  const handleCreateTask = async (riskActionId, activityId) => {
    setIsCreatingTask(riskActionId);

    try {
      const taskData = {
        process_id: process.id,
        process_activity_id: activityId,
        activity_risk_action_id: riskActionId,
      };

      const result = await taskService.createTask(taskData, authTokens.access);

      if (result.success) {
        showSuccess('Tâche assignée avec succès!');
        fetchProcessDetails();
      } else {
        if (result.status === 409) {
          showError('Cette tâche a déjà été assignée.');
        } else {
          showError(`Erreur: ${result.error || 'Impossible d\'assigner la tâche.'}`);
        }
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showError('Une erreur est survenue lors de la création de la tâche.');
    } finally {
      setIsCreatingTask(null);
    }
  };

  const canAssignTasks = (hasTask) => {
    return hasRole("RiskManager") && !hasTask;
  };

  const canViewHistory = () => {
    return hasRole("Admin");
  }

  const handleViewProcessHistory = (process) => {
    navigate(`/dashboard/admin/detailedLogs/process/${process.id}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-300 text-gray-800', label: 'Brouillon' },
      published: { color: 'bg-green-100 text-green-800', label: 'Publié' },
      archived: { color: 'bg-red-100 text-red-800', label: 'Archivé' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probConfig = {
      low: { color: 'bg-green-100 text-green-800', label: 'Mineur' },
      mineur: { color: 'bg-green-100 text-green-800', label: 'Mineur' },
      normal: { color: 'bg-yellow-100 text-yellow-800', label: 'Normal' },
      moyenne: { color: 'bg-yellow-100 text-yellow-800', label: 'Normal' },
      high: { color: 'bg-red-100 text-red-800', label: 'Majeur' },
      majeur: { color: 'bg-red-100 text-red-800', label: 'Majeur' },
      critique: { color: 'bg-red-200 text-red-800', label: 'Critique' },
      critical: { color: 'bg-red-200 text-red-800', label: 'Critique' }
    };
    
    const config = probConfig[probability?.toLowerCase()] || { color: 'bg-gray-100 text-gray-800', label: probability || 'Non définie' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const renderControlElements = (controlElements, rowKey) => {
    if (!controlElements || controlElements.length === 0) {
      return <span>-</span>;
    }

    const firstElement = controlElements[0];
    const remainingCount = controlElements.length - 1;

    return (
      <div className="flex items-center gap-2">
        <div>
          <div className="font-medium text-sm">{firstElement.element_name}</div>
          <div className="text-xs text-gray-500">{formatDate(firstElement.control_date)}</div>
        </div>
        
        {remainingCount > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowControlPopup(showControlPopup === rowKey ? null : rowKey)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
            >
              <Info className="w-3 h-3" />
              +{remainingCount}
            </button>

            {showControlPopup === rowKey && (
              <div
                ref={popupRef}
                className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-3 min-w-[250px]"
              >
                <div className="text-xs font-semibold text-gray-700 mb-2 pb-2 border-b">
                  Tous les éléments de contrôle ({controlElements.length})
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {controlElements.map((element, idx) => (
                    <div
                      key={element.id}
                      className={`p-2 rounded ${idx === 0 ? 'bg-blue-50' : 'bg-gray-50'}`}
                    >
                      <div className="font-medium text-sm text-gray-900">
                        {element.element_name}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Date: {formatDate(element.control_date)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!process) {
    return <div className="flex justify-center items-center h-32 text-sm">Chargement...</div>;
  }

  const canEditCartograph = () => {
    return hasRole("RiskManager");
  }

  const handleEditProcess = (process) => {
    navigate(`/dashboard/cartographies/edit/${process.id}`, { state: { process } });
  };

  const handleViewProcess = (process) => {
    navigate(`/dashboard/cartographies/${process.id}`);
  };

  const handleUpdateActivityRisk = (risk, action) => {
    setSelectedRisk(risk);
    setUpdateData({
      probability: risk.probability || '',
      consequence: risk.consequence || '',
      actionId: action.id || 0,
      action: action.action || '',
      dueDate: action.dueDate || '',
      assignedUser: action.assigned_user || null,
      selectedRole: action.assigned_user?.userRoleAssigned || null
    });
    setShowUpdateModal(true);
  };

  const handleSubmitUpdate = async (submittedData) => {
    try {
      showLoader('Mise à jour en cours...');
      
      const payload = {
        probability: submittedData.probability,
        consequence: submittedData.consequence,
        actionId: submittedData.actionId,
        action: submittedData.action,
        dueDate: submittedData.dueDate,
        assignedUserId: submittedData.assignedUserId,
        userRoleAssignedId: submittedData.userRoleAssignedId
      };
      
      const result = await processService.updateActivityRisk(
        selectedRisk.id,
        payload,
        authTokens.access
      );
      
      if (result.success) {
        let successMessage = 'Risque mis à jour avec succès!';
        
        // Check if there were task updates in the response
        if (result.data && result.data.task_updates && result.data.task_updates.tasks_updated > 0) {
          const taskCount = result.data.task_updates.tasks_updated;
          successMessage += ` ${taskCount} tâche(s) associée(s) ont été réassignées automatiquement.`;
        }
        
        showSuccess(successMessage);
        setShowUpdateModal(false);
        fetchProcessDetails();
      } else {
        showError(`Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating risk:', error);
      showError('Une erreur est survenue');
    } finally {
      hideLoader();
    }
  };

  const probabiliteOptions = [
    { value: 'mineur', label: 'Mineur', color: 'bg-blue-100 text-blue-800' },
    { value: 'normal', label: 'Normal', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'majeur', label: 'Majeur', color: 'bg-orange-100 text-orange-800' },
    { value: 'critique', label: 'Critique', color: 'bg-red-100 text-red-800' }
  ];

  return (
    <>
      {/* Loop through activities */}
      {process.associated_activities && process.associated_activities.length > 0 ? (
        process.associated_activities.map((activity) => (
          <>
            {/* Loop through risks in each activity */}
            {activity.activity_risks && activity.activity_risks.length > 0 ? (
              activity.activity_risks.map((risk) => (
                <>
                  {/* Loop through actions in each risk */}
                  {risk.actions && risk.actions.length > 0 ? (
                    risk.actions.map((action) => (
                      <tr key={`${activity.activity_id}-${risk.id}-${action.id}`} className="hover:bg-gray-50 transition-colors">
                        <td className="border border-gray-300 px-3 py-2">{process.process_name}</td>
                        <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.process_date)}</td>
                        <td className="border border-gray-300 px-3 py-2">
                          {renderControlElements(process.control_elements_list, `${activity.activity_id}-${risk.id}-${action.id}`)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2">{process.focal_point || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{getStatusBadge(process.status)}</td>
                        <td className="border border-gray-300 px-3 py-2">
                          {process.creator_details ? `${process.creator_details.first_name} ${process.creator_details.last_name}` : '-'}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.created_at)}</td>
                        <td className="border border-gray-300 px-3 py-2">
                          {process.last_updater_details ? `${process.last_updater_details.first_name} ${process.last_updater_details.last_name}` : '-'}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.updated_at)}</td>
                        <td className="border border-gray-300 px-3 py-2">{activity.activity_name}</td>
                        <td className="border border-gray-300 px-3 py-2 max-w-xs">{activity.activity_description || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{risk.risk_name}</td>
                        <td className="border border-gray-300 px-3 py-2 max-w-xs">{risk.description || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{getProbabilityBadge(risk.probability)}</td>
                        <td className="border border-gray-300 px-3 py-2">{risk.consequence}</td>
                        <td className="border border-gray-300 px-3 py-2">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {risk.status}
                          </span>
                        </td>
                        <td className="border border-gray-300 px-3 py-2 max-w-sm">{action.action}</td>
                        <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">
                          {isOverdue(action.dueDate) ? (
                            <span className="text-red-600 font-semibold flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(action.dueDate)}
                            </span>
                          ) : formatDate(action.dueDate)}
                        </td>
                        <td className="border border-gray-300 px-3 py-2">
                          {action.assigned_user ? `${action.assigned_user.first_name} ${action.assigned_user.last_name} (${action.assigned_user.email})` : '-'}
                        </td>
                        <td className="border border-gray-300 px-3 py-2">
                          {action.assigned_user.role_function}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-center">

                          {canAssignTasks(action.has_task) && showActions ? (
                            /* --- ASSIGN BUTTON --- */
                            <button
                              onClick={() => handleCreateTask(action.id, activity.activity_id)}
                              disabled={isLoading || isCreatingTask === action.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                            >
                              {isCreatingTask === action.id ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>...</span>
                                </>
                              ) : (
                                <>
                                  <UserPlus className="w-3 h-3" />
                                  <span>Assigner</span>
                                </>
                              )}
                            </button>

                          ) : action.has_task ? (

                            /* --- WHEN A TASK EXISTS --- */
                            action.task?.status === "completed" ? (
                              <span className="text-xs text-green-600 font-medium">Completed</span>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">✓ Assignée</span>
                            )

                          ) : (
                            '-' /* No task */
                          )}

                          {/* --- UPDATE BUTTON IF COMPLETED --- */}
                          {canEditCartograph() && (
                            <div>
                              {/* {action.task?.status === "completed" && ( */}
                                <button
                                  onClick={() => handleUpdateActivityRisk(risk, action)}
                                  disabled={isLoading}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-200 mt-2 rounded hover:bg-green-300 transition-colors disabled:bg-green-200 disabled:cursor-not-allowed"
                                >
                                  <Edit className="w-4 h-4 text-green-800" />
                                  <span className="text-green-800">Risque</span>
                                </button>
                              {/* )} */}
                            </div>
                          )}
                        </td>


                        {showActions && (
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <button 
                              onClick={() => handleViewProcess(process)}  
                              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                              <Eye className="h-4 w-4" />
                            </button>
                            {canEditCartograph() && (
                              <button 
                                onClick={() => handleEditProcess(process)}
                                className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded">
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canViewHistory() && (
                              <button 
                                onClick={() => handleViewProcessHistory(process)}
                                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                                title="Afficher l'historique"
                              >
                                <History className="h-4 w-4" />
                              </button> 
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    /* Risk has no actions */
                    <tr key={`${activity.activity_id}-${risk.id}-no-action`} className="hover:bg-gray-50 transition-colors">
                      <td className="border border-gray-300 px-3 py-2">{process.process_name}</td>
                      <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.process_date)}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        {renderControlElements(process.control_elements_list, `${activity.activity_id}-${risk.id}-no-action`)}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">{process.focal_point || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{getStatusBadge(process.status)}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        {process.creator_details ? `${process.creator_details.first_name} ${process.creator_details.last_name}` : '-'}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.created_at)}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        {process.last_updater_details ? `${process.last_updater_details.first_name} ${process.last_updater_details.last_name}` : '-'}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.updated_at)}</td>
                      <td className="border border-gray-300 px-3 py-2">{activity.activity_name}</td>
                      <td className="border border-gray-300 px-3 py-2 max-w-xs">{activity.activity_description || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{risk.risk_name}</td>
                      <td className="border border-gray-300 px-3 py-2 max-w-xs">{risk.description || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{getProbabilityBadge(risk.probability)}</td>
                      <td className="border border-gray-300 px-3 py-2">{risk.consequence}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {risk.status}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2">-</td>
                      <td className="border border-gray-300 px-3 py-2">-</td>
                      <td className="border border-gray-300 px-3 py-2">-</td>
                      <td className="border border-gray-300 px-3 py-2">-</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">-</td>
                    </tr>
                  )}
                </>
              ))
            ) : (
              /* Activity has no risks */
              <tr key={`${activity.activity_id}-no-risk`} className="hover:bg-gray-50 transition-colors">
                <td className="border border-gray-300 px-3 py-2">{process.process_name}</td>
                <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.process_date)}</td>
                <td className="border border-gray-300 px-3 py-2">
                  {renderControlElements(process.control_elements_list, `${activity.activity_id}-no-risk`)}
                </td>
                <td className="border border-gray-300 px-3 py-2">{process.focal_point || '-'}</td>
                <td className="border border-gray-300 px-3 py-2">{getStatusBadge(process.status)}</td>
                <td className="border border-gray-300 px-3 py-2">
                  {process.creator_details ? `${process.creator_details.first_name} ${process.creator_details.last_name}` : '-'}
                </td>
                <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.created_at)}</td>
                <td className="border border-gray-300 px-3 py-2">
                  {process.last_updater_details ? `${process.last_updater_details.first_name} ${process.last_updater_details.last_name}` : '-'}
                </td>
                <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.updated_at)}</td>
                <td className="border border-gray-300 px-3 py-2">{activity.activity_name}</td>
                <td className="border border-gray-300 px-3 py-2 max-w-xs">{activity.activity_description || '-'}</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2">-</td>
                <td className="border border-gray-300 px-3 py-2 text-center">-</td>
              </tr>
            )}
          </>
        ))
      ) : (
        /* Process has no activities */
        <tr className="hover:bg-gray-50 transition-colors">
          <td className="border border-gray-300 px-3 py-2">{process.process_name}</td>
          <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.process_date)}</td>
          <td className="border border-gray-300 px-3 py-2">
            {renderControlElements(process.control_elements_list, 'no-activity')}
          </td>
          <td className="border border-gray-300 px-3 py-2">{process.focal_point || '-'}</td>
          <td className="border border-gray-300 px-3 py-2">{getStatusBadge(process.status)}</td>
          <td className="border border-gray-300 px-3 py-2">
            {process.creator_details ? `${process.creator_details.first_name} ${process.creator_details.last_name}` : '-'}
          </td>
          <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.created_at)}</td>
          <td className="border border-gray-300 px-3 py-2">
            {process.last_updater_details ? `${process.last_updater_details.first_name} ${process.last_updater_details.last_name}` : '-'}
          </td>
          <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">{formatDate(process.updated_at)}</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2">-</td>
          <td className="border border-gray-300 px-3 py-2 text-center">-</td>
        </tr>
      )}

      {/* Update Risk Modal */}
      <UpdateRiskModal
        show={showUpdateModal}
        updateData={updateData}
        setUpdateData={setUpdateData}
        probabiliteOptions={probabiliteOptions}
        onClose={() => setShowUpdateModal(false)}
        onSubmit={handleSubmitUpdate}
        authTokens={authTokens}
      />
    </>
  );
}